import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { ChevronLeft } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { InternationalEventResponse } from 'api/responseTypes';
import { InternationalEventListItem } from 'components/InternationalEventListItem/InternationalEventListItem';
import { useEventTracking } from 'hooks/useEventTracking';
import { ROUTES } from 'router/routes';

/** Home chip shows fewer than five events; the rest live on the listing page. */
const PREVIEW_COUNT = 4;

interface InternationalFilteredEventsProps {
  items: InternationalEventResponse[];
  isLoading: boolean;
}

/**
 * The home "International Events" filtered view: a short compact list (<5 events)
 * with a "عرض الكل" button that opens the full international events page.
 */
export const InternationalFilteredEvents = ({
  items,
  isLoading,
}: InternationalFilteredEventsProps) => {
  const navigate = useNavigate();
  const { eventTracking } = useEventTracking();

  const handleClick = (event: InternationalEventResponse) => {
    eventTracking.internationalEvent.clicked({
      eventId: event.id,
      eventName: event.title,
      eventDate: event.date ?? null,
      venue: event.city ?? undefined,
      source: 'home_page',
    });
    navigate('/international-event/' + event.id);
  };

  if (isLoading) {
    return (
      <Flex align='center' justify='center' py={12}>
        <Spinner color='green.G50' size='xl' thickness='4px' />
      </Flex>
    );
  }

  if (items.length === 0) {
    return (
      <Flex align='center' justify='center' py={12}>
        <Text textStyle='bodyLargeRegular' color='dark.D500' textAlign='center'>
          <FormattedMessage defaultMessage='لا توجد فعاليات متاحة' id='homepage.events.noEvents' />
        </Text>
      </Flex>
    );
  }

  return (
    <Box maxW={{ base: '100%', md: '720px' }} mx='auto'>
      <Box as='ul' listStyleType='none' display='flex' flexDirection='column' gap={5} p={0}>
        {items.slice(0, PREVIEW_COUNT).map((event) => (
          <InternationalEventListItem
            key={event.id}
            event={event}
            onCardClick={() => handleClick(event)}
          />
        ))}
      </Box>

      {items.length > PREVIEW_COUNT && (
        <Flex justify='center' mt={6}>
          <Button
            variant='outline'
            borderColor='green.G500'
            color='green.G500'
            borderRadius='full'
            px={8}
            rightIcon={<ChevronLeft size={18} />}
            _hover={{ bg: 'green.G0' }}
            onClick={() => navigate(`/${ROUTES.internationalEvents}`)}
          >
            <FormattedMessage defaultMessage='عرض الكل' id='homepage.viewAll' />
          </Button>
        </Flex>
      )}
    </Box>
  );
};
